import { useDocumentTitle } from "@/lib/use-document-title";
import { useQuery } from "@tanstack/react-query";
import { StaffLayout } from "@/components/StaffLayout";
import { getDashboardStats } from "@/lib/admin.functions";
import { useAttendanceCounts, useMembers } from "@/lib/data-hooks";
import { useAuth } from "@/lib/auth-context";
import { Users, TrendingUp, TrendingDown, Banknote, Calendar, Sparkles, UserPlus, Activity, CheckCircle2 } from "lucide-react";



const statusLabels: Record<string, { label: string; tone: string }> = {
  aktiv: { label: "Aktiv", tone: "bg-emerald-500/15 text-emerald-700" },
  vantar_betalning: { label: "Väntar betalning", tone: "bg-amber-500/15 text-amber-700" },
  obetald: { label: "Obetald", tone: "bg-rose-500/15 text-rose-700" },
  pausad: { label: "Pausad", tone: "bg-sky-500/15 text-sky-700" },
  uppsagd: { label: "Uppsagd", tone: "bg-zinc-500/15 text-zinc-700" },
  inaktiv: { label: "Inaktiv", tone: "bg-zinc-400/15 text-zinc-500" },
};

function fmtSek(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

type Stats = Awaited<ReturnType<typeof getDashboardStats>>;

function DashboardPage() {
  useDocumentTitle("Översikt — ÅF-Portal");
  const { session } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard-stats"], queryFn: getDashboardStats, enabled: !!session });
  const { data: attendance } = useAttendanceCounts(!!session);
  const { data: allMembers = [] } = useMembers();
  const totalNights = attendance ? [...attendance.values()].reduce((s, n) => s + n, 0) : 0;
  const topNights = attendance
    ? [...attendance.entries()]
        .map(([id, nights]) => {
          const m = allMembers.find((x) => x.id === id);
          return { id, nights, name: m ? `${m.first_name} ${m.last_name}` : "Okänd medlem" };
        })
        .sort((a, b) => b.nights - a.nights)
        .slice(0, 5)
    : [];
  if (isLoading || !data) {

    return (
      <StaffLayout title="Ledningsöversikt">
        <p className="text-muted-foreground mb-8 max-w-2xl">Realtidsöversikt av medlemsbasen, intäkter och engagemang.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card-soft p-5 animate-pulse">
              <div className="h-4 w-4 rounded bg-primary/10 mb-3" />
              <div className="h-3 w-24 rounded bg-primary/10 mb-3" />
              <div className="h-6 w-20 rounded bg-primary/10 mb-1" />
              <div className="h-2.5 w-16 rounded bg-primary/10" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card-soft p-5 animate-pulse">
              <div className="h-4 w-4 rounded bg-primary/10 mb-3" />
              <div className="h-3 w-24 rounded bg-primary/10 mb-3" />
              <div className="h-6 w-20 rounded bg-primary/10 mb-1" />
              <div className="h-2.5 w-16 rounded bg-primary/10" />
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="card-soft p-6 lg:col-span-2 h-64 animate-pulse" />
          <div className="card-soft p-6 h-64 animate-pulse" />
        </div>
      </StaffLayout>
    );
  }

  const maxStore = Math.max(1, ...data.membersByStore.map((s: Stats["membersByStore"][number]) => s.members));
  const maxBenefit = Math.max(1, ...data.topBenefits.map((b: Stats["topBenefits"][number]) => b.count));
  const maxPartner = Math.max(1, ...data.topPartners.map((p: Stats["topPartners"][number]) => p.count));

  return (
    <StaffLayout title="Ledningsöversikt">
      <p className="text-muted-foreground mb-8 max-w-2xl">Realtidsöversikt av medlemsbasen, intäkter och engagemang.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Users} label="Aktiva medlemmar" value={data.kpi.activeMembers.toString()} hint={`${data.kpi.totalMembers} totalt`} />
        <Kpi icon={UserPlus} label="Nya / 30 dagar" value={data.kpi.newLast30.toString()} hint="Senaste månaden" accent="text-emerald-600" />
        <Kpi icon={TrendingDown} label="Avslutade / 30 dagar" value={data.kpi.cancelledLast30.toString()} hint={`${data.kpi.churnRate}% churn`} accent="text-rose-600" />
        <Kpi icon={Activity} label="Engagemang" value={`${data.topBenefits.reduce((s: number, b: Stats["topBenefits"][number]) => s + b.count, 0)}`} hint="Förmåner använda" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Banknote} label="MRR" value={fmtSek(data.kpi.mrr)} hint="Återkommande intäkt/mån" accent="text-primary" />
        <Kpi icon={TrendingUp} label="ARR (prognos)" value={fmtSek(data.kpi.arr)} hint="Årsintäkt baserat på MRR" accent="text-primary" />
        <Kpi icon={Sparkles} label="Betalande" value={data.kpi.payingMembers.toString()} hint="Aktiva + väntande" />
        <Kpi icon={Calendar} label="Eventanmälningar" value={data.upcoming.reduce((s: number, e: Stats["upcoming"][number]) => s + e.registered, 0).toString()} hint="Kommande event" />
        <Kpi icon={CheckCircle2} label="Närvaro totalt" value={totalNights.toString()} hint="Avprickade kvällar" accent="text-emerald-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="card-soft p-6 lg:col-span-2">
          <h2 className="!text-lg mb-5">Medlemmar per butik</h2>
          <div className="space-y-4">
            {data.membersByStore.map((s: Stats["membersByStore"][number]) => (
              <div key={s.store}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-display font-semibold">{s.store}</span>
                  <span className="text-muted-foreground">{s.members} aktiva</span>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary to-[var(--color-primary-hover,theme(colors.primary))] rounded-full transition-all" style={{ width: `${(s.members / maxStore) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-soft p-6">
          <h2 className="!text-lg mb-5">Status-fördelning</h2>
          <div className="space-y-2.5">
            {data.statusBreakdown.map((s: Stats["statusBreakdown"][number]) => {
              const meta = statusLabels[s.status];
              return (
                <div key={s.status} className="flex items-center justify-between">
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-display font-semibold ${meta.tone}`}>{meta.label}</span>
                  <span className="font-display font-bold text-lg">{s.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="card-soft p-6">
          <h2 className="!text-lg mb-5">Mest använda förmåner</h2>
          <div className="space-y-3">
            {data.topBenefits.map((b: Stats["topBenefits"][number]) => (
              <div key={b.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-display font-semibold">{b.label}</span>
                  <span className="text-muted-foreground">{b.count}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${(b.count / maxBenefit) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          {!!data.leastBenefits.length && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Minst använda</div>
              <div className="text-sm text-muted-foreground">{data.leastBenefits.map((b: Stats["leastBenefits"][number]) => `${b.label} (${b.count})`).join(" · ")}</div>
            </div>
          )}
        </div>

        <div className="card-soft p-6">
          <h2 className="!text-lg mb-5">Mest populära partnererbjudanden</h2>
          <div className="space-y-3">
            {data.topPartners.map((p: Stats["topPartners"][number]) => (
              <div key={p.name}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-display font-semibold">{p.name}</span>
                  <span className="text-muted-foreground">{p.count}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(p.count / maxPartner) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-soft p-6">
          <h2 className="!text-lg mb-5">Flest kvällar</h2>
          {topNights.length ? (
            <div className="space-y-2.5">
              {topNights.map((m, i) => (
                <div key={m.id} className="flex items-center justify-between">
                  <span className="text-sm font-display font-semibold">
                    <span className="text-muted-foreground mr-2">{i + 1}.</span>{m.name}
                  </span>
                  <span className="text-sm text-muted-foreground">{m.nights} {m.nights === 1 ? "kväll" : "kvällar"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Ingen närvaro registrerad än.</div>
          )}
        </div>
      </div>



      <div className="card-soft p-6">
        <h2 className="!text-lg mb-5">Kommande event & anmälningar</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {data.upcoming.map((e: Stats["upcoming"][number]) => {
            const fillRate = (e.registered / e.max_attendees) * 100;
            return (
              <div key={e.id} className="p-4 rounded-lg bg-muted">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-semibold text-sm">{e.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {e.store} · {new Date(e.starts_at).toLocaleDateString("sv-SE", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-primary">{e.registered}</div>
                    <div className="text-xs text-muted-foreground">/ {e.max_attendees}</div>
                  </div>
                </div>
                <div className="h-1.5 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, fillRate)}%` }} />
                </div>
              </div>
            );
          })}
          {!data.upcoming.length && <div className="text-sm text-muted-foreground">Inga kommande event.</div>}
        </div>
      </div>
    </StaffLayout>
  );
}

function Kpi({ icon: Icon, label, value, hint, accent }: { icon: any; label: string; value: string; hint?: string; accent?: string }) {
  return (
    <div className="card-soft p-5">
      <Icon className={`mb-2 ${accent ?? "text-primary"}`} size={20} />
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${accent ?? ""}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default DashboardPage;
